import React, { useEffect, useState } from 'react';
import { CheckCircle2, Circle, FlaskConical, Loader2, XCircle } from 'lucide-react';
import { MCP_PUBLIC_URL, SINGLETON_ENDPOINT_ID } from '../governedMcpConstants';
import type { McpClient } from '../governedMcpTypes';
import { FormSection, FormStack, GatewayDrawer, fieldClass, ghostBtn, labelClass, panelClass, primaryBtn } from '../components/GatewayUi';

type StepState = 'pending' | 'running' | 'passed' | 'failed';

const STEPS = ['Connection', 'Authentication', 'Discovery', 'Authorization', 'Response'] as const;

export default function TestConnectionModal({
  open,
  clients,
  isLight,
  onClose,
  onRun,
}: {
  open: boolean;
  clients: McpClient[];
  isLight: boolean;
  onClose: () => void;
  onRun: (clientId?: string) => { ok: boolean; message: string };
}) {
  const [clientId, setClientId] = useState('');
  const [states, setStates] = useState<StepState[]>(STEPS.map(() => 'pending'));
  const [message, setMessage] = useState<string | null>(null);
  const [running, setRunning] = useState(false);

  useEffect(() => {
    if (open) {
      setClientId('');
      setStates(STEPS.map(() => 'pending'));
      setMessage(null);
      setRunning(false);
    }
  }, [open]);

  const run = async () => {
    setRunning(true);
    setMessage(null);
    const result = onRun(clientId || undefined);
    const failAt = result.ok ? -1 : 1;
    const next = STEPS.map(() => 'pending' as StepState);
    for (let i = 0; i < STEPS.length; i += 1) {
      next[i] = 'running';
      setStates([...next]);
      await new Promise((resolve) => setTimeout(resolve, 280));
      if (failAt >= 0 && i >= failAt) {
        next[i] = i === failAt ? 'failed' : 'pending';
        setStates([...next]);
        setMessage(result.message);
        setRunning(false);
        return;
      }
      next[i] = 'passed';
      setStates([...next]);
    }
    setMessage(result.message);
    setRunning(false);
  };

  if (!open) return null;

  return (
    <GatewayDrawer
      open={open}
      eyebrow="Governed MCP / Test"
      icon={<FlaskConical size={18} className="text-indigo-500" />}
      title="Test MCP connection"
      subtitle="Handshake only. Discovery is still filtered by identity, client trust, and policy."
      status={{
        label: running ? 'Running' : message ? (states.includes('failed') ? 'Failed' : 'Complete') : 'Ready',
        tone: running ? 'neutral' : states.includes('failed') ? 'warn' : message ? 'success' : 'neutral',
      }}
      isLight={isLight}
      size="lg"
      onClose={onClose}
      footer={
        <>
          <button type="button" className={ghostBtn(isLight)} onClick={onClose}>Close</button>
          <button type="button" className={primaryBtn()} disabled={running} onClick={() => void run()}>
            {running ? 'Testing…' : 'Run test'}
          </button>
        </>
      }
    >
      <FormStack>
        <div className="space-y-5">
          <FormSection title="Client" description="Optional registered client for this handshake." isLight={isLight}>
            <div>
              <label className={labelClass(isLight)}>Client (optional)</label>
              <select className={fieldClass(isLight)} value={clientId} onChange={(e) => setClientId(e.target.value)}>
                <option value="">None</option>
                {clients.filter((client) => client.endpointId === SINGLETON_ENDPOINT_ID || !client.endpointId).map((client) => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </div>
          </FormSection>
          <FormSection title="Pipeline" description="Connection through authorization and response." isLight={isLight}>
            <ol className="space-y-3">
              {STEPS.map((step, index) => {
                const state = states[index];
                return (
                  <li key={step} className="flex items-center gap-3 text-sm">
                    {state === 'running' ? <Loader2 size={18} className="animate-spin text-indigo-400" /> : null}
                    {state === 'passed' ? <CheckCircle2 size={18} className="text-emerald-400" /> : null}
                    {state === 'failed' ? <XCircle size={18} className="text-red-400" /> : null}
                    {state === 'pending' ? <Circle size={18} className="text-neutral-500" /> : null}
                    <span className={isLight ? 'text-neutral-800' : 'text-neutral-200'}>{step}</span>
                  </li>
                );
              })}
            </ol>
            {message ? <p className={`text-sm mt-4 ${isLight ? 'text-neutral-600' : 'text-neutral-400'}`}>{message}</p> : null}
          </FormSection>
        </div>
        <section className={`${panelClass(isLight)} p-5`}>
          <h3 className={`text-sm font-semibold ${isLight ? 'text-neutral-900' : 'text-white'}`}>Remote MCP URL</h3>
          <p className={`mt-3 text-sm font-mono break-all ${isLight ? 'text-neutral-700' : 'text-neutral-200'}`}>
            {MCP_PUBLIC_URL}
          </p>
        </section>
      </FormStack>
    </GatewayDrawer>
  );
}
